import React, {useState} from 'react';
import gql from 'graphql-tag';
import {Route, Switch, useHistory, useLocation, useParams, useRouteMatch} from 'react-router';
import {useApolloClient, useQuery} from '@apollo/client';

import {Contact, DashboardNotification} from '../../../schema';

import UnexpectedErrorScene from '../../system/UnexpectedErrorScene';
import NotFoundScene from '../../system/NotFoundScene';
import IconEmail from '../../../components/icon/IconEmail';
import IconTrash from '../../../components/icon/IconTrash';
import ReactDocumentTitle from '../../../components/ReactDocumentTitle';
import AppLayout from '../../../layouts/AppLayout';
import OldUrls from '../../../models/utils/OldUrls';
import Avatar from '../../../ui/Avatar';
import DropdownActionMenu from '../../../ui/DropdownActionMenu';
import TextButton from '../../../ui/TextButton';
import H1 from '../../../ui/H1';
import DropdownMenuSection from '../../../ui/DropdownMenuSection'
import DropdownMenuItem from '../../../ui/DropdownMenuItem'
import UIContainer from '../../../ui/UIContainer';
import {reverse} from '../../../urls';
import {Notifications} from '../../../models/utils/Notifications';
import {UserContext} from '../../../utils/Context';
import {NavLink} from "react-router-dom";
import cn from "classnames";
import ContactDetailsSkeleton from "./ContactDetailsSkeleton";
import ParticipationHistoryTab from "../../../components/contact/ParticipationHistoryTab";
import DeleteContactDlg from "../../../components/contact/DeleteContactDlg";
import Announcement from "./Announcement";
import {addressToLines} from "../../../models/utils/Addresses";
import IconLocalPin from "../../../components/icon/IconLocalPin";
import IconMerge from "../../../components/icon/IconMerge";
import MergeDlg from "../../../components/contact/MergeDlg";
import ContactVisibilityStatus from "../../../ui/ContactVisibilityStatus";
import {EyeIcon, EyeOffIcon} from "@heroicons/react/outline";
import {showError, showInfo} from "../../../ui/ToastNotification";
import {GraphQLError} from "graphql";

interface QueryParams {
  id?: string
}

function useSearchQuery() {
  const {search} = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}


export default function ContactDetailScene() {

  const {id} = useParams<QueryParams>();
  const searchQuery = useSearchQuery();
  const history = useHistory();
  const match = useRouteMatch();
  const {search} = useLocation();

  const [showMergeDlg, setShowMergeDlg] = useState(false);
  const [showDeleteDlg, setShowDeleteDlg] = useState(false);
  const client = useApolloClient();

  const {loading, data, error} = useQuery<ContactDetailQuery>(query, {
    variables: {id, notificationId: Notifications.LIKE_NEW_CONTACT_PAGE}
  });

  if (error) {
    return <UnexpectedErrorScene
      error={error}
      errorInfo={null}
      eventId={null}
    />;
  }

  const goBack = () => {
    const defaultBack = () => history.goBack();
    switch (searchQuery.get('ref')) {
      case 'list':
        history.push(reverse('contacts_list'));
        break;
      default:
        defaultBack();
    }
  }

  const onChangeVisibility = (state: boolean) => {
    client.mutate({
      mutation: contactVisibilityMutation,
      variables: {id, paid: state},
      optimisticResponse: {
        toggleContactVisibility: {
          id,
          __typename: 'Contact',
          visible: state
        }
      }
    }).then(
      () => {
        showInfo('Visibility status has been changed');
      },
      (apolloError) => {
        apolloError.graphQLErrors.map((x: GraphQLError) => showError(x.message));
      }
    );
  }


  const addressLines = data?.contact.address ? addressToLines(data?.contact.address) : [];
  const filteredEmails: string[] = data?.contact.emails.filter(email => email !== data?.contact.email) || [];

  return (<>
      <ReactDocumentTitle title="Contact"/>
      <AppLayout className="" fullScreen={true}>
        <UserContext.Consumer>
          {user => data?.notification.visible && <Announcement user={user}/>}
        </UserContext.Consumer>
        <div className="absolute h-24 w-24"></div>
        {loading && <ContactDetailsSkeleton/>}
        {!loading && (!data || !data.contact) && <NotFoundScene/>}
        {!loading && !!data && data.contact && (
          <>
            <div className="bg-gray-light">
              <UIContainer>
                <TextButton className="text-left pl-0 mt-4" onClick={goBack}>&larr; Back</TextButton>
                <div className="flex mt-1 pb-10">
                  <Avatar name={data.contact.name} className="rounded-md w-28 h-28"/>
                  <div className="flex flex-1 flex-col ml-4 justify-between">
                    <H1>{data.contact.name}</H1>
                    <div className="flex mr-10 gap-x-4">
                      {addressLines.length > 0 &&
                        <span><IconLocalPin className="text-gray-dark inline -mt-0.5 mr-1"/> {addressLines.join(", ")}</span>
                      }
                      <ContactVisibilityStatus visible={data.contact.visible}/>
                    </div>
                    <div className="text-blue">
                      <IconEmail className="mr-1"/><a
                      href={"mailto:" + data.contact.email}>{data.contact.email}</a>
                      {filteredEmails.length > 0 && <>{filteredEmails.map(x => <>, <a key={x}
                                                                                      href={"mailto:" + x}>{x}</a></>)}</>}
                    </div>
                  </div>
                  <div className="flex flex-col ml-2">
                    <DropdownActionMenu
                      caption="Edit Contact"
                      onButtonClick={() => window.location.assign(OldUrls.editContact(data.contact.id, reverse('contacts_detail', {id: data.contact.id})))}
                    >
                      <DropdownMenuItem caption={data.contact.visible ? 'Make hidden' : 'Make visible'}
                                        onClick={() => onChangeVisibility(!data.contact.visible)}
                      >
                        {!data.contact.visible &&
                          <EyeIcon className="h-5 w-5 text-gray-dark inline-block -ml-1.5 mr-2.5"/>}
                        {data.contact.visible &&
                          <EyeOffIcon className="h-5 w-5 text-gray-dark inline-block -ml-1.5 mr-2.5"/>}
                      </DropdownMenuItem>
                      <DropdownMenuItem caption="Merge" onClick={() => setShowMergeDlg(true)}>
                        <IconMerge className="text-gray-dark -ml-1.5 mr-3.5"/>
                      </DropdownMenuItem>
                      <DropdownMenuSection className="pt-2 pb-0">
                        <DropdownMenuItem caption="Delete contact" className="text-red"
                                          onClick={() => setShowDeleteDlg(true)}>
                          <IconTrash className="-ml-1 mr-3"/>
                        </DropdownMenuItem>
                      </DropdownMenuSection>
                    </DropdownActionMenu>
                  </div>
                </div>
                <NavLink exact to={reverse('contacts_detail', {id: data.contact.id}) + search}
                         className={(isActive: boolean) => cn("font-bold mr-6 outline-none py-2 inline-block",
                           {"border-b-4 border-blue text-black": isActive, "text-blue": !isActive})}
                >
                  Participation history
                </NavLink>
              </UIContainer>
            </div>
            <UIContainer className="pb-10">
              <Switch>
                <Route exact path={`${match.path}`}>
                  <ParticipationHistoryTab contact={data.contact}/>
                </Route>
              </Switch>
              <MergeDlg contact={data.contact} show={showMergeDlg} setShowDialog={setShowMergeDlg}/>
              <DeleteContactDlg id={data.contact.id}
                                name={data.contact.name} show={showDeleteDlg} setShowDialog={setShowDeleteDlg}/>
            </UIContainer>
          </>
        )
        }
      </AppLayout>
    </>
  )
    ;
}

export interface ContactDetailQuery {
  contact: Contact
  notification: Pick<DashboardNotification, "id" | "type" | "visible">
}

const query = gql`
  query ContactDetailQuery($id: ID!, $notificationId: ID!) {

    contact: contact(contactId: $id) {
      id
      name
      email
      emails
      type
      events
      visible
      address {
        postcode
        street1
        street2
        city
        country
      }
      attendance {
        eventTitle
        eventId
        eventFree
        startDate
        paidAmount
        paidCurrency
        paid
        status
        attendeeId
      }
    }
    notification: notification(id: $notificationId) {
      id
      type
      visible
    }
  }
`;

const contactVisibilityMutation = gql`
  mutation ToggleContactVisibility($id: ID!) {
    toggleContactVisibility(contactId: $id) {
      id
      visible
    }
  }
`;
